import React, { useCallback, useMemo, useState, useEffect } from 'react'
import Alert from '@mui/material/Alert'
import useMediaQuery from '@mui/material/useMediaQuery'

import TabWithDropdown from '../../layout/TabWithDropdown'
import TabsMui from '../../layout/TabsMui'
import { layoutMobileMediaQuery } from '../../layout/layoutConstants'

import RefundDetailsTab from './RefundDetailsTab'
import RefundSalesmanDetailsTab from './RefundSalesmanDetailsTab'
import RefundApprovalDetail from '../approval/RefundApprovalDetail'
import { REFUND_APPROVAL_TEXT } from '../constants/refundApprovalText'
import {
  UI_COLOR_SUCCESS_EMPHASIS,
  UI_COLOR_SUCCESS_EMPHASIS_BG,
  UI_COLOR_SUCCESS_EMPHASIS_TEXT
} from '../../constants/uiColors'
import { isRefundApprovalWorkflowEnabled } from '../../global_context/capabilities/refundCapabilities'
import { buildRefundTabs, REFUND_STATUS_UI_MAP, REFUND_TAB_VALUES } from './shared/refundConstants'

const getRefundTypeTitle = (refundType) => {
  switch (refundType) {
    case 'refund':
      return 'Rechazo total'
    case 'partial_refund':
      return 'Rechazo parcial'
    default:
      return 'Devolución'
  }
}

const getRefundStatusUI = (activity = {}) => {
  const refundType = activity?.refund_type

  if (refundType === 'refund') return REFUND_STATUS_UI_MAP.refund
  if (refundType === 'partial_refund') return REFUND_STATUS_UI_MAP.partial_refund

  return REFUND_STATUS_UI_MAP?.[activity?.status] || null
}

const getActionAlertPreview = () => {
  if (typeof window === 'undefined') return null

  const previewAction = new URLSearchParams(window.location.search).get('action_alert')
  return ['approve', 'reject', 'ncr', 'duplicate'].includes(previewAction) ? previewAction : null
}

const ACTION_ALERT_DURATION_MS = 5000
const ACTION_ALERT_SEVERITY_STYLE = {
  success: {
    border: UI_COLOR_SUCCESS_EMPHASIS,
    borderWidth: 1,
    background: UI_COLOR_SUCCESS_EMPHASIS_BG,
    text: UI_COLOR_SUCCESS_EMPHASIS_TEXT,
    icon: UI_COLOR_SUCCESS_EMPHASIS,
    fontWeight: 500
  },
  warning: {
    border: '#FFA45B',
    borderWidth: 2,
    background: '#FFF3E6',
    text: '#6F3D09',
    icon: '#F57C00',
    fontWeight: 700
  }
}

const buildActionAlert = (activity = {}, action) => {
  const refundRef = activity?.refund_db_ref || activity?.uuid || ''

  if (action === 'duplicate') {
    return {
      id: Date.now(),
      severity: 'warning',
      message: REFUND_APPROVAL_TEXT.alreadyFinalizedError
    }
  }

  const message =
    action === 'ncr'
      ? 'El NCR ha sido registrado exitosamente.'
      : action === 'reject'
        ? `La devolución ${refundRef} ha sido rechazada.`
        : `La devolución ${refundRef} ha sido procesada exitosamente.`

  return {
    id: Date.now(),
    severity: 'success',
    message
  }
}

const buildWarningActionAlert = (message) => ({
  id: Date.now(),
  severity: 'warning',
  message
})

export default function ShowRefundView({ details, defaultDetails, onRefresh }) {
  const [activity, setActivity] = useState(defaultDetails)
  const [activeTab, setActiveTab] = useState(REFUND_TAB_VALUES.REFUND)
  const [actionAlert, setActionAlert] = useState(() => {
    const previewAction = getActionAlertPreview()
    return previewAction ? buildActionAlert(defaultDetails, previewAction) : null
  })
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)

  useEffect(() => {
    setActivity(defaultDetails)
  }, [defaultDetails])

  useEffect(() => {
    const previewAction = getActionAlertPreview()
    setActionAlert(previewAction ? buildActionAlert(defaultDetails, previewAction) : null)
  }, [defaultDetails?.uuid])

  useEffect(() => {
    setActiveTab(REFUND_TAB_VALUES.REFUND)
  }, [activity?.refund_type])

  useEffect(() => {
    if (!actionAlert) return undefined

    const dismissTimer = window.setTimeout(() => {
      setActionAlert(null)
    }, ACTION_ALERT_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [actionAlert])

  const tabs = useMemo(() => buildRefundTabs(), [])
  const isSalesmanRefund = activity?.refund_type === 'salesman_refund'
  const useApprovalWorkflow = isSalesmanRefund && isRefundApprovalWorkflowEnabled()
  const statusUI = getRefundStatusUI(activity)
  const handleRefundActionSuccess = useCallback(
    (action) => {
      setActionAlert(buildActionAlert(activity, action))
    },
    [activity?.refund_db_ref, activity?.uuid]
  )
  const handleRefundActionError = useCallback((message) => {
    setActionAlert(buildWarningActionAlert(message))
  }, [])

  if (!activity) return null

  const actionAlertStyle = actionAlert
    ? ACTION_ALERT_SEVERITY_STYLE[actionAlert.severity] || ACTION_ALERT_SEVERITY_STYLE.success
    : ACTION_ALERT_SEVERITY_STYLE.success

  return (
    <>
      <div
        className="main-show-header"
        style={{
          position: 'relative',
          marginBottom: useApprovalWorkflow ? 35 : isMobileLayout ? (actionAlert ? 16 : 24) : 40
        }}
      >
        <h2
          style={{
            float: 'none',
            margin: 0,
            height: 'auto',
            ...(isMobileLayout
              ? {
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              : {})
          }}
        >
          {getRefundTypeTitle(activity?.refund_type)}: {activity?.refund_db_ref}
        </h2>
        {actionAlert && (
          <Alert
            key={actionAlert.id}
            severity={actionAlert.severity || 'success'}
            onClose={() => setActionAlert(null)}
            sx={{
              position: isMobileLayout ? 'relative' : 'absolute',
              right: isMobileLayout ? undefined : 0,
              top: isMobileLayout ? undefined : '50%',
              transform: isMobileLayout ? undefined : 'translateY(-50%)',
              zIndex: 1,
              overflow: 'hidden',
              width: isMobileLayout ? '100%' : 'fit-content',
              maxWidth: '100%',
              minHeight: isMobileLayout ? 36 : 40,
              mx: 0,
              mt: isMobileLayout ? 1.25 : 0,
              mb: 0,
              px: isMobileLayout ? 1.5 : 2,
              py: isMobileLayout ? 1 : 1.25,
              border: `${actionAlertStyle.borderWidth}px solid ${actionAlertStyle.border}`,
              borderRadius: '4px',
              backgroundColor: actionAlertStyle.background,
              color: actionAlertStyle.text,
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              '@keyframes actionAlertProgress': {
                from: { transform: 'scaleX(1)' },
                to: { transform: 'scaleX(0)' }
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '100%',
                height: 3,
                backgroundColor: actionAlertStyle.border,
                transformOrigin: 'left center',
                animation: `actionAlertProgress ${ACTION_ALERT_DURATION_MS}ms linear forwards`,
                pointerEvents: 'none'
              },
              '& .MuiAlert-icon': {
                color: actionAlertStyle.icon,
                fontSize: isMobileLayout ? 20 : 22,
                p: 0,
                mr: isMobileLayout ? 1 : 1.25,
                opacity: 1
              },
              '& .MuiAlert-message': {
                flex: '1 1 auto',
                py: 0,
                pr: isMobileLayout ? 1 : 1.5,
                fontSize: isMobileLayout ? 13 : 15,
                fontWeight: actionAlertStyle.fontWeight,
                lineHeight: 1.35,
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-line'
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
                color: actionAlertStyle.text,
                m: 0,
                p: 0,
                pl: 0.5,
                ml: 0
              },
              '& .MuiIconButton-root': {
                p: 0.25,
                color: actionAlertStyle.text
              },
              '& .MuiSvgIcon-root': {
                fontSize: isMobileLayout ? 18 : 20
              }
            }}
          >
            {actionAlert.message}
          </Alert>
        )}
      </div>

      <div className={`index-table ${useApprovalWorkflow ? 'refund-approval-index-table' : ''}`}>
        <div className="show-area">
          {isSalesmanRefund ? (
            useApprovalWorkflow ? (
              <RefundApprovalDetail
                activity={activity}
                onRefresh={onRefresh}
                onActionSuccess={handleRefundActionSuccess}
                onActionError={handleRefundActionError}
                isMobileLayout={isMobileLayout}
              />
            ) : (
              <>
                <div className="show-area-tabs-header">
                  <TabsMui
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    scrollable={false}
                    showDivider={false}
                    marginBottom={0}
                  />
                  {!isMobileLayout && (
                    <div className="status-orders">
                      <b className="order-status-b">Estado:</b>
                      <span className={`order-status ${statusUI?.className || ''}`}>
                        {statusUI?.label || activity?.status || '-'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="tab-content-area">
                  {activeTab === REFUND_TAB_VALUES.REFUND && (
                    <RefundSalesmanDetailsTab
                      activity={activity}
                      statusUI={statusUI}
                      showStatusInHeader={isMobileLayout}
                    />
                  )}
                </div>
              </>
            )
          ) : (
            <div>
              <div className="status-orders">
                <b className="order-status-b">Estado:</b>
                <span className={`order-status ${statusUI?.className || ''}`}>
                  {statusUI?.label || activity?.status || '-'}
                </span>
              </div>
              <div>
                {(activity?.refund_type === 'refund' ||
                  activity?.refund_type === 'partial_refund') && (
                  <ul className="nav nav-tabs">
                    <TabWithDropdown name="Entrega" activeTab={'Entrega'} items={[]} />
                  </ul>
                )}
              </div>
              <div>
                <RefundDetailsTab activity={activity} transactionUuid={details?.uuid} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
